import type { UserInfo } from 'firebase/auth'
import React, { useEffect, useMemo, useState } from 'react'
import EmailLoginForm from '~/src/components/EmailLoginForm'
import useAuthUser from '~/src/hooks/useAuthUser'
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  SupportedProviderId,
} from '~/src/lib/firebase/firebaseAuth'
import AuthButton from './AuthButton'

interface ProviderButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  providerId?: SupportedProviderId
  loading?: boolean
  size?: 'sm' | 'md' | 'lg'
  hideOtherButtons?: boolean
  pendingEmailAuth?: boolean
  clearPendingEmailAuth?: () => void
}

const ProviderButton: React.FC<ProviderButtonProps> = ({
  children,
  className,
  onClick,
  providerId,
  loading,
  size = 'lg',
  hideOtherButtons,
  pendingEmailAuth,
  clearPendingEmailAuth,
}) => {
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const [providerUserInfo, setProviderUserInfo] = useState<UserInfo>()
  const [selectedProvider, setSelectedProvider] =
    useState<SupportedProviderId>()

  const providerData = useMemo(
    () => authUser?.providerData || [],
    [authUser?.providerData]
  )

  const isGoogleAuth = providerId === GoogleAuthProvider.PROVIDER_ID
  const isEmailAuth = providerId === EmailAuthProvider.PROVIDER_ID

  const showLoader = loading && selectedProvider === providerId

  useEffect(() => {
    setProviderUserInfo(providerData.find((pd) => pd.providerId === providerId))
  }, [providerData, providerId])

  if (loadingAuthUser || (hideOtherButtons && !isEmailAuth)) return null

  if (pendingEmailAuth && isEmailAuth) {
    return <EmailLoginForm clearPendingEmailAuth={clearPendingEmailAuth} />
  }

  return (
    <AuthButton
      size={size}
      isGoogleAuth={isGoogleAuth}
      providerId={providerId}
      providerUserInfo={providerUserInfo}
      providerData={providerData}
      showLoader={showLoader}
      className={className}
      onClick={(event) => {
        setSelectedProvider(providerId)
        onClick && onClick(event)
      }}
    >
      {children}
    </AuthButton>
  )
}

export default ProviderButton
